import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useActivities, useAllGear, useBabyMilestones, useGrowth, useBabyPhotos, useDeletePhotoEntry, useDeleteBabyMedia, useQuestionnaires } from '../../data/queries';
import { babyCaps } from '../../data/permissions';
import { rowsToMeasurements, resolveMilestoneMedia } from '../../data/derive';
import type { Baby, Measurement } from '../../data/types';
import type { ActivityCategory, BabyMilestoneRow, GearItemRow, MediaRow, QuestionnaireItem } from '../../data/dto';
import { QuestionnaireCard } from '../questionnaires/QuestionnaireCard';
import { QuestionnaireSheet } from '../questionnaires/QuestionnaireSheet';
import {
  ACTIVITY_META,
  placeholderActivities,
  type ActivityKind,
  type PlaceholderActivity,
} from '../../data/activitiesPlaceholder';
import { Icon } from '../chrome/Icon';
import { AvatarButton } from '../chrome/Avatar';
import { hash, rng } from '../shared/rng';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useMediaBlob, useCatalogOrBlob } from '../media/useMediaBlob';
import { MediaLightbox } from '../media/MediaLightbox';
import { useT, type TFn } from '../../i18n';

interface Props {
  baby: Baby;
  onOpenDetail: (id: string) => void;
  onOpenActivity?: (id: number) => void;
  onNavigateToday: () => void;
  onOpenProfile?: () => void;
  onOpenMedia?: () => void;
  onAddPhotos?: () => void;
  onNavigateGrowth?: () => void;
}

const MAX_WEEKS = 104;
// Stable empty-gear reference so the entries memo doesn't churn before the
// lazy gear query resolves.
const EMPTY_GEAR: Record<number, GearItemRow[]> = {};

/* Decorative winding dashed path, repeated down the centre of the spine. */
const WINDING_PATH =
  "url(\"data:image/svg+xml,%3Csvg width='120' height='420' viewBox='0 0 120 420' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M60 0C60 52 18 105 18 157C18 210 102 262 102 315C102 367 60 420 60 472' stroke='%23211C15' stroke-opacity='0.13' stroke-width='2' stroke-dasharray='7 9' stroke-linecap='round'/%3E%3C/svg%3E\")";

function daysFromDob(dobIso: string | undefined, dateIso: string | undefined): number {
  if (!dobIso || !dateIso) return 0;
  const dob = new Date(dobIso.slice(0, 10) + 'T00:00:00Z').getTime();
  const at = new Date(dateIso.slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.max(0, (at - dob) / 86_400_000);
}

function weeksFromDob(dobIso: string | undefined, dateIso: string): number {
  if (!dobIso) return 0;
  const dob = new Date(dobIso.slice(0, 10) + 'T00:00:00Z').getTime();
  const at = new Date(dateIso.slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.max(0, Math.floor((at - dob) / (7 * 86_400_000)));
}

function ageLabel(t: TFn, week: number): string {
  if (week === 0) return t('journey.birth', 'Birth');
  return t('journey.weekLabel', 'Week {n}').replace('{n}', String(week));
}

/** Until the backend serves primary media, derive a stable image/video flag. */
function mediaKind(row: BabyMilestoneRow): 'image' | 'video' | 'audio' {
  if (row.primary_media_type) return row.primary_media_type;
  return hash(`media-${row.milestone_id}`) % 4 === 0 ? 'video' : 'image';
}

/* ── Entries ──────────────────────────────────────────────────────────────── */

type Entry =
  | { kind: 'birth'; week: number; dayOffset: number; sortKey: string }
  | { kind: 'measurement'; week: number; dayOffset: number; sortKey: string; measurement: Measurement }
  | { kind: 'activity'; week: number; dayOffset: number; sortKey: string; activity: PlaceholderActivity }
  | { kind: 'questionnaire'; week: number; dayOffset: number; sortKey: string; item: QuestionnaireItem }
  | { kind: 'achieved'; week: number; dayOffset: number; sortKey: string; row: BabyMilestoneRow }
  | { kind: 'photos'; week: number; dayOffset: number; sortKey: string; photoEntry: any }
  | {
      kind: 'today';
      week: number;
      dayOffset: number;
      sortKey: string;
      inProgress: BabyMilestoneRow[];
      overdue: BabyMilestoneRow[];
    }
  | { kind: 'expected'; week: number; dayOffset: number; sortKey: string; row: BabyMilestoneRow; gear: GearItemRow[] };

const RANK: Record<Entry['kind'], number> = {
  birth: 0,
  measurement: 1,
  activity: 2,
  photos: 2.5,
  questionnaire: 2.7,
  achieved: 3,
  today: 4,
  expected: 5,
};

export function JourneyTimeline({ baby, onOpenDetail, onOpenActivity, onNavigateToday, onOpenProfile, onOpenMedia, onAddPhotos, onNavigateGrowth }: Props) {
  const babyKey = baby.id ? `babysteps.journey.${baby.id}` : null;
  const msQ = useBabyMilestones(baby.id ?? null);
  const gearQ = useAllGear();
  const growthQ = useGrowth(baby.id ?? null);
  const actsQ = useActivities(baby.id ?? null);
  const photosQ = useBabyPhotos(baby.id ?? null);
  const questionnairesQ = useQuestionnaires(baby.id ?? null);
  const [openQuestionnaire, setOpenQuestionnaire] = useState<QuestionnaireItem | null>(null);

  const measurements = useMemo(
    () => rowsToMeasurements(growthQ.data ?? [], baby.dobIso),
    [growthQ.data, baby.dobIso],
  );

  // Completed activities placed on the path by their done date. Falls back to
  // the deterministic placeholder feed until the parent has logged any.
  const CAT_TO_KIND: Record<ActivityCategory, ActivityKind> = {
    play: 'play', sing: 'sing', music: 'sing', read: 'read', excursion: 'go_out',
  };
  const doneActivities = useMemo<(PlaceholderActivity & { done_date?: string })[]>(() => {
    const done = (actsQ.data ?? []).filter((a) => a.state === 'done' && a.done_date);
    return done.map((a) => ({
      id: `done-${a.id}`,
      kind: CAT_TO_KIND[a.category],
      title: a.title,
      week: weeksFromDob(baby.dobIso, a.done_date as string),
      activityId: a.id,
      done_media: a.done_media,
      done_date: a.done_date as string,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actsQ.data, baby.dobIso]);

  const rows = useMemo(() => msQ.data?.milestones ?? [], [msQ.data]);
  const gearData = gearQ.data ?? EMPTY_GEAR;
  const ageWeeks = Math.min(baby.ageWeeks, MAX_WEEKS);

  const entries = useMemo((): Entry[] => {
    const list: Entry[] = [{ kind: 'birth', week: 0, dayOffset: 0, sortKey: 'birth' }];

    for (const m of measurements) {
      const dayOffset = daysFromDob(baby.dobIso, m.date);
      const w = Math.min(Math.floor(dayOffset / 7), MAX_WEEKS);
      list.push({ kind: 'measurement', week: w, dayOffset, sortKey: m.id, measurement: m });
    }

    const activityFeed = doneActivities.length > 0 ? doneActivities : placeholderActivities(ageWeeks);
    for (const a of activityFeed) {
      const dayOffset = 'done_date' in a && a.done_date ? daysFromDob(baby.dobIso, a.done_date as string) : a.week * 7;
      const w = Math.min(a.week, MAX_WEEKS);
      list.push({ kind: 'activity', week: w, dayOffset, sortKey: String(a.activityId || a.id), activity: a });
    }

    const photoList = (photosQ.data ?? []).slice().sort((a, b) => {
      const dDiff = (a.taken_date || '').localeCompare(b.taken_date || '');
      if (dDiff !== 0) return dDiff;
      const cDiff = (a.created_at || '').localeCompare(b.created_at || '');
      if (cDiff !== 0) return cDiff;
      return (a.id || '').localeCompare(b.id || '');
    });
    for (const p of photoList) {
      const dayOffset = daysFromDob(baby.dobIso, p.taken_date);
      const w = Math.min(weeksFromDob(baby.dobIso, p.taken_date), MAX_WEEKS);
      list.push({
        kind: 'photos',
        week: w,
        dayOffset,
        sortKey: `${p.taken_date}_${p.created_at || ''}_${p.id}`,
        photoEntry: p,
      });
    }

    // Questionnaires that are available to fill or already completed, placed at
    // their trigger age. Locked (future) ones stay off the timeline.
    for (const q of questionnairesQ.data?.questionnaires ?? []) {
      if (q.availability === 'locked') continue;
      const dayOffset = q.trigger_date ? daysFromDob(baby.dobIso, q.trigger_date) : q.trigger_week * 7;
      const w = Math.min(q.trigger_week, MAX_WEEKS);
      list.push({ kind: 'questionnaire', week: w, dayOffset, sortKey: q.code, item: q });
    }

    const inProgress: BabyMilestoneRow[] = [];
    const overdue: BabyMilestoneRow[] = [];

    for (const row of rows) {
      if (row.status === 'achieved') {
        const dayOffset = row.achieved_date
          ? daysFromDob(baby.dobIso, row.achieved_date)
          : row.median_achieved_weeks * 7;
        const w = row.achieved_date
          ? Math.min(weeksFromDob(baby.dobIso, row.achieved_date), MAX_WEEKS)
          : Math.min(Math.round(row.median_achieved_weeks), MAX_WEEKS);
        list.push({
          kind: 'achieved',
          week: w,
          dayOffset,
          sortKey: `${row.achieved_date || ''}_${row.milestone_id}`,
          row,
        });
      } else if (row.status === 'in_progress') {
        inProgress.push(row);
      } else {
        const limitWeeks = row.median_achieved_weeks - row.std_dev_weeks;
        if (ageWeeks > limitWeeks) {
          overdue.push(row);
        } else {
          const dayOffset = row.median_achieved_weeks * 7;
          list.push({
            kind: 'expected',
            week: Math.min(Math.round(row.median_achieved_weeks), MAX_WEEKS),
            dayOffset,
            sortKey: `${row.median_achieved_weeks}_${row.milestone_id}`,
            row,
            gear: (gearData[row.milestone_id] ?? []) as GearItemRow[],
          });
        }
      }
    }

    const todayIso = new Date().toISOString().slice(0, 10);
    const todayDayOffset = baby.dobIso ? daysFromDob(baby.dobIso, todayIso) : ageWeeks * 7;
    list.push({
      kind: 'today',
      week: ageWeeks,
      dayOffset: todayDayOffset,
      sortKey: 'today',
      inProgress,
      overdue,
    });

    return list.sort((a, b) => {
      if (a.dayOffset !== b.dayOffset) return a.dayOffset - b.dayOffset;
      if (RANK[a.kind] !== RANK[b.kind]) return RANK[a.kind] - RANK[b.kind];
      return a.sortKey.localeCompare(b.sortKey);
    });
  }, [rows, measurements, gearData, baby.dobIso, ageWeeks, doneActivities, photosQ.data, questionnairesQ.data]);

  const currentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const savedY = sessionStorage.getItem(`${babyKey}.scrollY`);
    if (savedY !== null && Number(savedY) > 0) {
      window.scrollTo(0, Number(savedY));
    } else {
      const t = setTimeout(
        () => currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        120,
      );
      return () => clearTimeout(t);
    }
  }, [babyKey]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const handleScroll = () => {
      sessionStorage.setItem(`${babyKey}.scrollY`, String(window.scrollY));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [babyKey]);

  // Quarter-year stops for the right-edge scrubber, with a distinct "Now"
  // marker dropped in at the baby's current age.
  const scrubberStops = useMemo(() => {
    if (entries.length === 0) return [];
    
    const maxW = Math.max(...entries.map((e) => e.week), ageWeeks);

    const nearestId = (target: number): string => {
      let bestEntry = entries[0];
      let bestDiff = Math.abs(bestEntry.week - target);
      for (const e of entries) {
        const diff = Math.abs(e.week - target);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestEntry = e;
        }
      }
      return entryKey(bestEntry);
    };

    const weeksPerMonth = 4.345;
    interface ScrubStop {
      id: string;
      label: string;
      isToday: boolean;
      week: number;
    }

    const stops: ScrubStop[] = [{ id: 'birth', label: 'Birth', isToday: false, week: 0 }];
    for (let month = 3; month <= 24; month += 3) {
      const w = Math.round(month * weeksPerMonth);
      if (w > maxW + 2) break;
      stops.push({ id: nearestId(w), label: `${month}m`, isToday: false, week: w });
    }

    stops.push({ id: 'today', label: 'Now', isToday: true, week: ageWeeks });
    stops.sort((a, b) => a.week - b.week);

    const result: ScrubStop[] = [];
    for (const stop of stops) {
      const last = result[result.length - 1];
      if (last && Math.abs(last.week - stop.week) < 4) {
        if (stop.isToday) {
          result[result.length - 1] = stop;
        }
        continue;
      }
      if (result.some((s) => s.id === stop.id)) {
        if (stop.isToday) {
          const idx = result.findIndex((s) => s.id === stop.id);
          result[idx] = stop;
        }
        continue;
      }
      result.push(stop);
    }
    return result;
  }, [entries, ageWeeks]);

  const handleScrub = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (msQ.isLoading || photosQ.isLoading) {
    return (
      <div className="flex flex-col pb-24">
        <Header baby={baby} onOpenProfile={onOpenProfile} onOpenMedia={onOpenMedia} onAddPhotos={onAddPhotos} />
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-ink-3">Loading journey…</p>
        </div>
      </div>
    );
  }

  // Side cards alternate; centred cards (photos/today/expected/birth) don't
  // consume a side slot, so we track the counter separately.
  let sideIdx = 0;

  return (
    <div className="relative flex flex-col pb-24">
      <Header baby={baby} onOpenProfile={onOpenProfile} onOpenMedia={onOpenMedia} onAddPhotos={onAddPhotos} />

      <div
        className="relative mt-2"
        style={{ backgroundImage: WINDING_PATH, backgroundRepeat: 'repeat-y', backgroundPosition: 'center top' }}
      >
        {entries.map((entry) => {
          const key = entryKey(entry);
          let contentNode;

          if (entry.kind === 'birth') {
            contentNode = <BirthNode baby={baby} />;
          } else if (entry.kind === 'measurement') {
            const side = sideIdx++ % 2 === 0 ? 'left' : 'right';
            contentNode = (
              <SideRow side={side} week={entry.week}>
                <MeasurementCard measurement={entry.measurement} />
              </SideRow>
            );
          } else if (entry.kind === 'activity') {
            const firstPhoto = entry.activity.done_media?.find((m) => m.media_type === 'image');
            if (firstPhoto) {
              contentNode = (
                <ActivityMilestoneNode
                  activity={entry.activity}
                  week={entry.week}
                  onOpenActivity={onOpenActivity}
                />
              );
            } else {
              const side = sideIdx++ % 2 === 0 ? 'left' : 'right';
              contentNode = (
                <SideRow side={side} week={entry.week}>
                  <ActivityCard activity={entry.activity} onOpenActivity={onOpenActivity} />
                </SideRow>
              );
            }
          } else if (entry.kind === 'questionnaire') {
            const side = sideIdx++ % 2 === 0 ? 'left' : 'right';
            contentNode = (
              <SideRow side={side} week={entry.week}>
                <QuestionnaireCard
                  item={entry.item}
                  questions={questionnairesQ.data?.questions[entry.item.question_set] ?? []}
                  babyId={baby.id ?? ''}
                  onOpen={() => setOpenQuestionnaire(entry.item)}
                />
              </SideRow>
            );
          } else if (entry.kind === 'photos') {
            contentNode = (
              <PhotoEntryNode
                photoEntry={entry.photoEntry}
                week={entry.week}
                baby={baby}
              />
            );
          } else if (entry.kind === 'achieved') {
            contentNode = (
              <AchievedNode
                row={entry.row}
                week={entry.week}
                dobIso={baby.dobIso}
                onOpenDetail={onOpenDetail}
              />
            );
          } else if (entry.kind === 'today') {
            contentNode = (
              <TodayNode
                week={entry.week}
                inProgress={entry.inProgress}
                overdue={entry.overdue}
                onOpenDetail={onOpenDetail}
                onNavigateToday={onNavigateToday}
                onNavigateGrowth={onNavigateGrowth}
              />
            );
          } else {
            contentNode = (
              <ExpectedNode row={entry.row} gear={entry.gear} onOpenDetail={onOpenDetail} />
            );
          }

          if (entry.kind === 'today') {
            return (
              <div key={key} ref={currentRef} id={key}>
                {contentNode}
              </div>
            );
          }

          return (
            <div key={key} id={key}>
              {contentNode}
            </div>
          );
        })}

        <EndNode />
      </div>

      {openQuestionnaire && baby.id && (
        <QuestionnaireSheet
          open
          baby={baby}
          item={openQuestionnaire}
          questions={questionnairesQ.data?.questions[openQuestionnaire.question_set] ?? []}
          onClose={() => setOpenQuestionnaire(null)}
          onOpenMedia={onOpenMedia}
        />
      )}

      {scrubberStops.length > 0 && (
        <nav
          className="fixed right-3 nav:right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-1 p-2 bg-surface/75 backdrop-blur-md border border-line/10 shadow-lg rounded-full select-none"
          aria-label="Timeline navigation"
        >
          {scrubberStops.map((stop) => {
            if (stop.isToday) {
              return (
                <button
                  key={stop.id}
                  onClick={() => handleScrub(stop.id)}
                  title="Today"
                  className="w-7 h-7 flex items-center justify-center rounded-full text-[9px] font-bold tracking-wider uppercase text-white shadow-sm active:scale-90 transition-all cursor-pointer outline-none"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                >
                  Now
                </button>
              );
            }
            return (
              <button
                key={stop.id}
                onClick={() => handleScrub(stop.id)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-[9px] font-bold tracking-wider text-ink-3 hover:text-ink hover:bg-grey-soft/40 active:scale-90 transition-all cursor-pointer outline-none"
              >
                {stop.label === 'Birth' ? 'B' : stop.label}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

function entryKey(e: Entry): string {
  switch (e.kind) {
    case 'birth':
      return 'birth';
    case 'measurement':
      return `meas-${e.measurement.id}`;
    case 'activity':
      return `act-${e.activity.id}`;
    case 'questionnaire':
      return `q-${e.item.code}`;
    case 'photos':
      return `photo-${e.photoEntry.id}`;
    case 'achieved':
      return `ach-${e.row.milestone_id}`;
    case 'today':
      return 'today';
    case 'expected':
      return `exp-${e.row.milestone_id}`;
  }
}

/* ── Header ─────────────────────────────────────────────────────────────────── */

function Header({ baby, onOpenProfile, onOpenMedia, onAddPhotos }: { baby: Baby; onOpenProfile?: () => void; onOpenMedia?: () => void; onAddPhotos?: () => void }) {
  const t = useT();
  return (
    <header className="mb-6 flex items-start justify-between gap-3">
      <div>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">{t('journey.eyebrow', 'Journey')}</p>
        <h1 className="mt-1 font-serif text-[28px] nav:text-3xl leading-tight tracking-tight text-ink">
          {t('journey.title', "{name}'s path").replace('{name}', baby.name)}
        </h1>
        <p className="mt-1 text-sm text-ink-2">{t('journey.subtitle', 'Birth through 24 months')}</p>
      </div>
      <div className="flex items-center gap-2">
        {onAddPhotos && (
          <button
            onClick={onAddPhotos}
            aria-label="Add photos"
            className="flex size-10 items-center justify-center rounded-full border border-line bg-surface text-ink-2 hover:text-ink active:scale-95 transition-all"
          >
            <Icon name="plus" size={18} />
          </button>
        )}
        {onOpenMedia && (
          <button
            onClick={onOpenMedia}
            aria-label="Media Gallery"
            className="flex size-10 items-center justify-center rounded-full border border-line bg-surface text-ink-2 hover:text-ink active:scale-95 transition-all"
          >
            <Icon name="photos" size={18} />
          </button>
        )}
        <div className="nav:hidden">
          {onOpenProfile && <AvatarButton size={40} baby={baby} onOpenProfile={onOpenProfile} />}
        </div>
      </div>
    </header>
  );
}

/* ── Spine dots ─────────────────────────────────────────────────────────────── */

function CenterDot({ variant }: { variant: 'birth' | 'achieved' | 'today' | 'future' | 'small' }) {
  const base =
    'absolute left-1/2 -translate-x-1/2 z-10 flex items-center justify-center rounded-full';
  if (variant === 'birth') {
    return (
      <div className={`${base} size-6`} style={{ background: 'var(--color-accent)' }}>
        <div className="size-2.5 rounded-full bg-white" />
      </div>
    );
  }
  if (variant === 'achieved') {
    return (
      <div className={`${base} size-6`} style={{ background: 'var(--color-accent)' }}>
        <Icon name="check" size={13} color="white" />
      </div>
    );
  }
  if (variant === 'today') {
    return (
      <div
        className={`${base} size-7 border-2`}
        style={{ borderColor: 'var(--color-accent)', background: 'var(--color-bg)' }}
      >
        <div className="size-3 animate-pulse rounded-full" style={{ background: 'var(--color-accent)' }} />
      </div>
    );
  }
  if (variant === 'future') {
    return (
      <div
        className={`${base} size-5 border-[1.5px] border-dashed`}
        style={{ borderColor: 'var(--color-grey)', background: 'var(--color-bg)' }}
      />
    );
  }
  return (
    <div
      className={`${base} size-3.5 border-2`}
      style={{ borderColor: 'var(--color-surface)', background: 'var(--color-grey)' }}
    />
  );
}

/* ── Birth ──────────────────────────────────────────────────────────────────── */

function BirthNode({ baby }: { baby: Baby }) {
  const t = useT();
  return (
    <div className="relative flex flex-col items-center pb-12 pt-2 text-center">
      <CenterDot variant="birth" />
      <div className="mt-10">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-ink">
          {t('journey.beginning', 'The beginning')}
        </span>
        <h2 className="mt-1.5 font-serif text-2xl tracking-tight text-ink">{baby.name}</h2>
        <p className="mt-0.5 text-sm italic text-ink-2">{baby.dob}</p>
      </div>
    </div>
  );
}

/* ── Side rows (measurement / activity) ───────────────────────────────────────── */

function SideRow({
  side,
  week,
  children,
}: {
  side: 'left' | 'right';
  week: number;
  children: ReactNode;
}) {
  const t = useT();
  const label = (
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-3">
      {ageLabel(t, week)}
    </span>
  );
  return (
    <div className="relative mb-7 flex items-center">
      <CenterDot variant="small" />
      {side === 'left' ? (
        <>
          <div className="flex w-1/2 justify-end pr-5 nav:pr-9">{children}</div>
          <div className="flex w-1/2 items-center pl-5 nav:pl-9">{label}</div>
        </>
      ) : (
        <>
          <div className="flex w-1/2 items-center justify-end pr-5 nav:pr-9 text-right">{label}</div>
          <div className="flex w-1/2 justify-start pl-5 nav:pl-9">{children}</div>
        </>
      )}
    </div>
  );
}

function MeasurementCard({ measurement: m }: { measurement: Measurement }) {
  const parts: string[] = [];
  if (m.weight !== null) parts.push(`${m.weight} kg`);
  if (m.height !== null) parts.push(`${m.height} cm`);
  if (m.head !== null) parts.push(`head ${m.head} cm`);
  return (
    <div className="w-full max-w-[180px] rounded-2xl border border-line bg-surface px-3.5 py-3 shadow-[0_8px_22px_-14px_rgba(33,28,21,0.22)]">
      <div className="flex items-center gap-1.5">
        <Icon name="growth" size={13} color="var(--color-ink-2)" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-3">
          Measured · {m.dateShort}
        </span>
      </div>
      <div className="mt-1.5 flex flex-col gap-0.5">
        {parts.length > 0 ? (
          parts.map((p) => (
            <span key={p} className="text-sm font-semibold text-ink">
              {p}
            </span>
          ))
        ) : (
          <span className="text-sm text-ink-2">Recorded</span>
        )}
      </div>
    </div>
  );
}

function ActivityCard({
  activity,
  onOpenActivity,
}: {
  activity: PlaceholderActivity;
  onOpenActivity?: (id: number) => void;
}) {
  const meta = (activity.kind && ACTIVITY_META[activity.kind]) || {
    icon: 'play' as const,
    label: activity.kind || 'Play',
    color: '#D98E5A',
  };
  const isClickable = !!activity.activityId && !!onOpenActivity;

  const cardContent = (
    <>
      <div className="flex items-center gap-2">
        <div
          className="flex size-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: `${meta.color}1f` }}
        >
          <Icon name={meta.icon} size={15} color={meta.color} />
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-[0.08em]"
          style={{ color: meta.color }}
        >
          {meta.label}
        </span>
      </div>
      <p className="mt-1.5 text-sm font-medium leading-snug text-ink">{activity.title}</p>
    </>
  );

  if (isClickable) {
    return (
      <button
        onClick={() => activity.activityId && onOpenActivity(activity.activityId)}
        className="w-full max-w-[180px] rounded-2xl border border-line bg-surface px-3.5 py-3 text-left shadow-[0_8px_22px_-14px_rgba(33,28,21,0.22)] hover:border-ink-3 hover:shadow-[0_12px_28px_-10px_rgba(33,28,21,0.3)] transition-all duration-200 active:scale-[0.97] cursor-pointer block"
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div className="w-full max-w-[180px] rounded-2xl border border-line bg-surface px-3.5 py-3 shadow-[0_8px_22px_-14px_rgba(33,28,21,0.22)]">
      {cardContent}
    </div>
  );
}

function ActivityMilestoneNode({
  activity,
  week,
  onOpenActivity,
}: {
  activity: PlaceholderActivity;
  week: number;
  onOpenActivity?: (id: number) => void;
}) {
  const t = useT();
  const enc = useEncryption();
  const meta = (activity.kind && ACTIVITY_META[activity.kind]) || {
    icon: 'play' as const,
    label: activity.kind || 'Play',
    color: '#D98E5A',
  };
  const tilt = (hash(`tilt-act-${activity.activityId}`) % 5) - 2; // -2…+2 deg

  const firstPhoto = activity.done_media?.find((m) => m.media_type === 'image');
  const { url, state } = useMediaBlob(
    firstPhoto ?? ({} as MediaRow),
    enc.status === 'ready',
    enc.decrypt,
    !!firstPhoto,
    true,
  );

  return (
    <div className="relative flex flex-col items-center pb-12">
      <CenterDot variant="achieved" />
      <button
        onClick={() => activity.activityId && onOpenActivity?.(activity.activityId)}
        className="group mt-9 w-full max-w-[280px] rounded-2xl bg-white p-2.5 pb-3 text-left shadow-[0_14px_36px_-12px_rgba(33,28,21,0.28)] transition-transform duration-300 active:scale-[0.98]"
        style={{ transform: `rotate(${tilt}deg)` }}
      >
        {url && state === 'ready' ? (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-tint">
            <img src={url} alt={activity.title} className="size-full object-cover" />
          </div>
        ) : (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-tint flex items-center justify-center">
            <div className="size-12 rounded-full flex items-center justify-center" style={{ background: `${meta.color}1f` }}>
              <Icon name={meta.icon} size={24} color={meta.color} />
            </div>
          </div>
        )}
        <div className="px-1.5 pt-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name={meta.icon} size={11} color={meta.color} />
            <span
              className="text-[9.5px] font-bold uppercase tracking-wider"
              style={{ color: meta.color }}
            >
              {meta.label}
            </span>
          </div>
          <p className="font-serif text-[19px] italic leading-tight text-ink">{activity.title}</p>
          <div className="mt-2">
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
              {ageLabel(t, week)}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

/* ── Achieved (polaroid photo) ─────────────────────────────────────────────────── */

function AchievedNode({
  row,
  week,
  dobIso,
  onOpenDetail,
}: {
  row: BabyMilestoneRow;
  week: number;
  dobIso?: string;
  onOpenDetail: (id: string) => void;
}) {
  const t = useT();
  const enc = useEncryption();
  const dateLabel = row.achieved_date
    ? new Date(row.achieved_date + 'T00:00:00Z').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;
  const tilt = (hash(`tilt-${row.milestone_id}`) % 5) - 2; // -2…+2 deg
  const kind = mediaKind(row);

  const media = useMemo(() => resolveMilestoneMedia(row), [row]);
  const { url, state } = useCatalogOrBlob(
    media
      ? ({
          id: media.id,
          mime_type: media.mimeType,
          is_e2e_encrypted: media.isEncrypted,
        } as MediaRow)
      : ({} as MediaRow),
    !!media?.isCatalog,
    enc.status === 'ready',
    enc.decrypt,
    !!media,
    true,
  );

  const achievedAgeWeeks = useMemo(() => {
    if (!dobIso || !row.achieved_date) return row.median_achieved_weeks;
    const dob = new Date(dobIso + 'T00:00:00Z').getTime();
    const ach = new Date(row.achieved_date + 'T00:00:00Z').getTime();
    return Math.max(0, (ach - dob) / (7 * 86_400_000));
  }, [row, dobIso]);

  const performance = useMemo(() => {
    const start = row.avg_start_weeks;
    const median = row.median_achieved_weeks;
    const lateLimit = row.median_achieved_weeks + row.std_dev_weeks;
    const flagLimit = row.early_flag_weeks;

    if (achievedAgeWeeks < start) {
      return { label: 'Very Early', bg: 'rgba(16, 185, 129, 0.12)', text: '#047857' };
    } else if (achievedAgeWeeks < median) {
      return { label: 'Early', bg: 'rgba(52, 211, 153, 0.12)', text: '#059669' };
    } else if (achievedAgeWeeks < lateLimit) {
      return { label: 'In the Median', bg: 'var(--color-grey-soft)', text: 'var(--color-ink-2)' };
    } else if (achievedAgeWeeks < flagLimit) {
      return { label: 'A Little Late', bg: 'var(--color-amber-soft)', text: 'var(--color-amber-ink)' };
    } else {
      return { label: 'Risk / Flag', bg: 'rgba(239, 68, 68, 0.12)', text: '#DC2626' };
    }
  }, [row, achievedAgeWeeks]);

  return (
    <div className="relative flex flex-col items-center pb-12">
      <CenterDot variant="achieved" />
      <button
        onClick={() => onOpenDetail(String(row.milestone_id))}
        className="group mt-9 w-full max-w-[280px] rounded-2xl bg-white p-2.5 pb-3 text-left shadow-[0_14px_36px_-12px_rgba(33,28,21,0.28)] transition-transform duration-300 active:scale-[0.98]"
        style={{ transform: `rotate(${tilt}deg)` }}
      >
        {url && state === 'ready' ? (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-tint">
            <img src={url} alt={row.name} className="size-full object-cover" />
          </div>
        ) : (
          <ProceduralPhoto seed={`${row.milestone_id}-${row.name}`} kind={kind} />
        )}
        <div className="px-1.5 pt-2.5">
          <p className="font-serif text-[19px] italic leading-tight text-ink">{row.name}</p>
          <div className="mt-2 flex items-center gap-1.5 text-pretty flex-wrap">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
              style={{ background: performance.bg }}
            >
              <Icon name="check" size={11} color={performance.text} />
              <span
                className="text-[9.5px] font-bold uppercase tracking-wider"
                style={{ color: performance.text }}
              >
                {performance.label}
              </span>
            </span>
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
              {dateLabel ?? ageLabel(t, week)}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

/** Seeded blob "photo" placeholder shown until real primary media is wired. */
function ProceduralPhoto({ seed, kind }: { seed: string; kind: 'image' | 'video' | 'audio' }) {
  const r = rng(hash(seed));
  const blobs = Array.from({ length: 3 }, (_, i) => {
    const r1 = Math.round(40 + r() * 25);
    const r2 = Math.round(40 + r() * 25);
    const r3 = Math.round(40 + r() * 25);
    const r4 = Math.round(40 + r() * 25);
    const size = 120 + Math.round(r() * 120);
    const top = Math.round(-30 + r() * 160);
    const left = Math.round(-40 + r() * 300);
    const tint = [0.16, 0.28, 0.4][i]!;
    return {
      size,
      top,
      left,
      radius: `${r1}% ${100 - r1}% ${r2}% ${100 - r2}% / ${r3}% ${r4}% ${100 - r3}% ${100 - r4}%`,
      tint,
    };
  });
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-tint">
      {blobs.map((b, i) => (
        <div
          key={i}
          className="absolute mix-blend-multiply"
          style={{
            width: b.size,
            height: b.size,
            top: b.top,
            left: b.left,
            borderRadius: b.radius,
            background: `color-mix(in oklch, var(--color-accent) ${Math.round(b.tint * 100)}%, var(--color-bg))`,
          }}
        />
      ))}
      {kind === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex size-11 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--color-ink)">
              <path d="M5 3.5v9l8-4.5z" />
            </svg>
          </div>
        </div>
      )}
      {kind === 'audio' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex size-11 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-ink">
            <Icon name="sing" size={18} color="currentColor" />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Today ──────────────────────────────────────────────────────────────────── */

function TodayNode({
  week,
  inProgress,
  overdue,
  onOpenDetail,
  onNavigateToday,
  onNavigateGrowth,
}: {
  week: number;
  inProgress: BabyMilestoneRow[];
  overdue: BabyMilestoneRow[];
  onOpenDetail: (id: string) => void;
  onNavigateToday: () => void;
  onNavigateGrowth?: () => void;
}) {
  const tr = useT();

  return (
    <div className="relative flex flex-col items-center pb-12">
      <CenterDot variant="today" />
      <div
        className="mt-10 w-full max-w-[330px] rounded-2xl border bg-surface p-4 shadow-[0_16px_40px_-14px_rgba(33,28,21,0.26)]"
        style={{ borderColor: 'color-mix(in oklch, var(--color-accent) 35%, transparent)' }}
      >
        <div className="mb-3 flex items-center gap-2">
          <span
            className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
            style={{ background: 'var(--color-accent)' }}
          >
            {tr('today.title', 'Today')}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-3">
            {ageLabel(tr, week)}
          </span>
        </div>

        {inProgress.length > 0 && (
          <div className="mb-3">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-ink">
              In progress
            </p>
            <div className="flex flex-col gap-1.5">
              {inProgress.map((row) => (
                <button
                  key={row.milestone_id}
                  onClick={() => onOpenDetail(String(row.milestone_id))}
                  className="flex items-center gap-2.5 rounded-xl border border-amber bg-amber-soft px-3 py-2 text-left transition-opacity active:opacity-80"
                >
                  <div
                    className="flex size-6 flex-shrink-0 items-center justify-center rounded-full"
                    style={{ background: 'var(--color-amber)' }}
                  >
                    <Icon name="arrow" size={12} color="white" />
                  </div>
                  <span className="flex-1 text-sm font-semibold text-ink">{row.name}</span>
                  <Icon name="chevron" size={14} color="var(--color-amber-ink)" />
                </button>
              ))}
            </div>
          </div>
        )}

        {overdue.length > 0 && (
          <div className="mb-3">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-3">
              Ready to explore
            </p>
            <div className="flex flex-col">
              {overdue.map((row, i) => (
                <button
                  key={row.milestone_id}
                  onClick={() => onOpenDetail(String(row.milestone_id))}
                  className={[
                    'flex items-center gap-2.5 px-1 py-2 text-left transition-colors',
                    i < overdue.length - 1 ? 'border-b border-hairline' : '',
                  ].join(' ')}
                >
                  <span className="size-1.5 flex-shrink-0 rounded-full" style={{ background: 'var(--color-grey)' }} />
                  <span className="flex-1 text-sm text-ink">{row.name}</span>
                  <Icon name="chevron" size={13} color="var(--color-ink-3)" />
                </button>
              ))}
            </div>
          </div>
        )}

        {inProgress.length === 0 && overdue.length === 0 && (
          <p className="mb-3 text-sm text-ink-2">
            All caught up — nothing emerging right now. Check back soon.
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            onClick={onNavigateToday}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-opacity active:opacity-80 cursor-pointer"
            style={{ background: 'var(--color-accent)' }}
          >
            <Icon name="today" size={16} color="white" />
            {tr('journey.today.openToday', "Open today's screen")}
          </button>
          {onNavigateGrowth && (
            <button
              onClick={onNavigateGrowth}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-line bg-surface px-4 py-2.5 text-sm font-semibold text-ink-2 hover:bg-grey-soft hover:text-ink transition-all active:scale-[0.98] cursor-pointer"
            >
              <Icon name="growth" size={16} color="var(--color-accent)" />
              {tr('journey.today.trackGrowth', 'Track growth')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Expected (future) ─────────────────────────────────────────────────────────── */

function ExpectedNode({
  row,
  gear,
  onOpenDetail,
}: {
  row: BabyMilestoneRow;
  gear: GearItemRow[];
  onOpenDetail: (id: string) => void;
}) {
  const t = useT();
  const enc = useEncryption();
  const media = useMemo(() => resolveMilestoneMedia(row), [row]);
  const { url, state } = useCatalogOrBlob(
    media
      ? ({
          id: media.id,
          mime_type: media.mimeType,
          is_e2e_encrypted: media.isEncrypted,
        } as MediaRow)
      : ({} as MediaRow),
    !!media?.isCatalog,
    enc.status === 'ready',
    enc.decrypt,
    !!media,
    true,
  );

  const kind = mediaKind(row);
  // Label from the true median so milestones clamped to the 104-week cap for
  // positioning still read their real expected age (e.g. "25.1 months").
  const label = ageLabel(t, Math.round(row.median_achieved_weeks));
  return (
    <div className="relative flex flex-col items-center pb-10">
      <CenterDot variant="future" />
      <button
        onClick={() => onOpenDetail(String(row.milestone_id))}
        className="mt-8 flex w-full max-w-[300px] items-center gap-3 rounded-2xl border-2 border-dashed bg-surface/60 p-3 text-left transition-colors hover:bg-surface active:bg-surface"
        style={{ borderColor: 'color-mix(in oklch, var(--color-grey) 70%, transparent)' }}
      >
        <div className="relative size-20 flex-shrink-0 overflow-hidden rounded-xl opacity-70 grayscale-[35%]">
          {url && state === 'ready' ? (
            <img src={url} alt={row.name} className="size-full object-cover" />
          ) : (
            <ProceduralPhoto seed={`${row.milestone_id}-${row.name}`} kind={kind} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-3">
            Coming up · {label}
          </p>
          <p className="mt-0.5 truncate text-sm font-semibold text-ink">{row.name}</p>
          {gear.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {gear.slice(0, 2).map((g) => (
                <span
                  key={g.id}
                  className="rounded-full bg-grey-soft px-2 py-0.5 text-[9.5px] font-medium text-ink-2"
                >
                  {g.item_name}
                </span>
              ))}
              {gear.length > 2 && (
                <span className="rounded-full bg-grey-soft px-2 py-0.5 text-[9.5px] font-medium text-ink-3">
                  +{gear.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        <Icon name="chevron" size={16} color="var(--color-ink-3)" style={{ flexShrink: 0 }} />
      </button>
    </div>
  );
}

/* ── End ────────────────────────────────────────────────────────────────────── */

function EndNode() {
  return (
    <div className="relative flex flex-col items-center pt-2">
      <div
        className="size-4 rounded-full border-[1.5px] border-dashed"
        style={{ borderColor: 'var(--color-grey)', background: 'var(--color-bg)' }}
      />
      <p className="mt-2 text-xs italic text-ink-3">24 months · the path continues</p>
    </div>
  );
}

/* ── Photo Entry Node (independent polaroid moments) ────────────────────────── */

interface PhotoEntryNodeProps {
  photoEntry: {
    id: string;
    title: string | null;
    description?: string | null;
    taken_date: string;
    created_at: string;
    media: any[];
  };
  week: number;
  baby: Baby;
}

function PhotoEntryNode({ photoEntry, week, baby }: PhotoEntryNodeProps) {
  const t = useT();
  const enc = useEncryption();
  const deleteMutation = useDeletePhotoEntry();
  const deletePhotoMedia = useDeleteBabyMedia();
  const caps = babyCaps(baby);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const dateLabel = photoEntry.taken_date
    ? new Date(photoEntry.taken_date + 'T00:00:00Z').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const tilt = (hash(`tilt-${photoEntry.id}`) % 5) - 2; // -2…+2 deg

  const mediaList = photoEntry.media || [];
  const activeMedia = mediaList[activeIndex];

  const { url, state } = useMediaBlob(
    activeMedia
      ? ({
          id: activeMedia.id,
          mime_type: activeMedia.mime_type,
          is_e2e_encrypted: activeMedia.is_e2e_encrypted,
        } as MediaRow)
      : ({} as MediaRow),
    enc.status === 'ready',
    enc.decrypt,
    !!activeMedia,
    true,
  );

  const mediaRows = useMemo(() => {
    return mediaList.map((m: any) => ({
      id: m.id,
      media_type: m.media_type,
      mime_type: m.mime_type,
      is_e2e_encrypted: m.is_e2e_encrypted,
      width_px: m.width_px,
      height_px: m.height_px,
      file_size_bytes: m.file_size_bytes,
      created_at: m.created_at,
    } as MediaRow));
  }, [mediaList]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this moment?')) {
      deleteMutation.mutate({ babyId: baby.id!, photoId: photoEntry.id });
    }
  };

  const isDeleting = deleteMutation.isPending;

  return (
    <div className="relative flex flex-col items-center pb-12">
      <CenterDot variant="small" />
      <div
        className="group mt-9 w-full max-w-[280px] rounded-2xl bg-white p-2.5 pb-3 text-left shadow-[0_14px_36px_-12px_rgba(33,28,21,0.28)] transition-transform duration-300"
        style={{ transform: `rotate(${tilt}deg)` }}
      >
        <button
          onClick={() => setLightboxIndex(activeIndex)}
          className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-tint text-left block"
        >
          {url && state === 'ready' ? (
            activeMedia.media_type === 'video' ? (
              <div className="relative size-full">
                <video src={url} muted playsInline className="size-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                  <div className="flex size-9 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="var(--color-ink)">
                      <path d="M5 3.5v9l8-4.5z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <img src={url} alt="" className="size-full object-cover" />
            )
          ) : state === 'loading' ? (
            <div className="flex size-full items-center justify-center bg-tint">
              <span className="size-5 animate-pulse rounded-full bg-ink-3/20" />
            </div>
          ) : state === 'locked' ? (
            <div className="flex size-full items-center justify-center bg-tint text-ink-3">
              <Icon name="lock" size={20} />
            </div>
          ) : (
            <div className="flex size-full items-center justify-center bg-tint text-ink-3">
              <Icon name="photos" size={20} />
            </div>
          )}

          {/* Navigation Arrows for Carousel */}
          {mediaList.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 shadow-sm text-ink hover:bg-white active:scale-90 transition-all z-20"
              >
                <span className="-scale-x-100 flex items-center justify-center">
                  <Icon name="chevron" size={14} />
                </span>
              </button>
              <button
                onClick={handleNext}
                aria-label="Next image"
                className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 shadow-sm text-ink hover:bg-white active:scale-90 transition-all z-20"
              >
                <Icon name="chevron" size={14} />
              </button>
              
              {/* Pagination Dots */}
              <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1 z-20">
                {mediaList.map((_: any, idx: number) => (
                  <span
                    key={idx}
                    className={[
                      'size-1.5 rounded-full transition-all duration-300',
                      idx === activeIndex ? 'bg-white scale-110 shadow-sm' : 'bg-white/40',
                    ].join(' ')}
                  />
                ))}
              </div>
            </>
          )}
        </button>

        <div className="px-1.5 pt-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-serif text-[18px] italic leading-tight text-ink break-words">
                {photoEntry.title || 'Captured moment'}
              </p>
              {photoEntry.description && (
                <p className="mt-1 text-[13px] leading-relaxed text-ink-2 whitespace-pre-wrap break-words">
                  {photoEntry.description}
                </p>
              )}
              <p className="mt-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
                {dateLabel} · {ageLabel(t, week)}
              </p>
            </div>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete moment"
              className="flex size-7 flex-shrink-0 items-center justify-center rounded-full hover:bg-red-50 text-ink-3 hover:text-red-600 active:scale-95 transition-all disabled:opacity-50"
            >
              {isDeleting ? (
                <span className="size-3.5 animate-spin rounded-full border-[1.5px] border-ink-3 border-t-transparent" />
              ) : (
                <Icon name="trash" size={14} />
              )}
            </button>
          </div>
        </div>
      </div>

      {lightboxIndex !== null && (
        <MediaLightbox
          items={mediaRows}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          canDecrypt={enc.status === 'ready'}
          decrypt={enc.decrypt}
          babyId={baby.id}
          entityType="journal_photo"
          entityId={photoEntry.id}
          onDelete={caps.canWrite && baby.id ? (m) => deletePhotoMedia.mutateAsync({ mediaId: m.id, babyId: baby.id! }) : undefined}
        />
      )}
    </div>
  );
}
